import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SPEED_TRADES_WS_BASE } from '@/lib/api/speed';
import { useWebSocket } from '@/lib/ws';

import type {
    TradeNotificationConnected,
    TradeNotificationError,
    TradeNotificationExecutionPhase,
    TradeNotificationMessage,
    TradeNotificationUpdate,
} from '../types';

import { useV2TradeNotifications } from './useV2TradeNotifications';

const KEEPALIVE_INTERVAL_MS = 30_000;

/** The session key prefix of the V2 (browser wallet) flow: `v2:<address>`, never a JWT. */
const V2_SESSION_KEY_PREFIX = 'v2:';

function parseTradeNotification(event: MessageEvent): TradeNotificationMessage {
    return JSON.parse(event.data as string) as TradeNotificationMessage;
}

interface UseTradeNotificationsOptions {
    networkId: number;
    token: string | null;
    onTradeUpdate?: (update: TradeNotificationUpdate) => void;
    onConnected?: (msg: TradeNotificationConnected) => void;
    onError?: (msg: TradeNotificationError) => void;
    onExecutionPhase?: (msg: TradeNotificationExecutionPhase) => void;
    onReconnect?: () => void;
}

/**
 * Wraps `useWebSocket` for the authenticated trade notifications channel.
 *
 * Connects to `wss://.../speed/trades/ws/:networkId?token=jwt` when
 * both `token` and real-mode conditions are met (non-null token).
 *
 * Sends a JSON keepalive ping `{ type: 3 }` every 30 seconds while
 * connected. Fires `onReconnect` when the connection transitions from
 * `reconnecting` to `connected`, enabling REST reconciliation.
 *
 * A V2 session key (`v2:<address>`) is not a JWT: the V1 socket stays closed
 * (URL `null`) and `useV2TradeNotifications` drives the same callbacks instead.
 */
export function useTradeNotifications({
    networkId,
    token,
    onTradeUpdate,
    onConnected,
    onError,
    onExecutionPhase,
    onReconnect,
}: UseTradeNotificationsOptions) {
    const isV2 = token?.startsWith(V2_SESSION_KEY_PREFIX) ?? false;

    const url = useMemo(() => {
        if (!token || isV2) return null;
        return `${SPEED_TRADES_WS_BASE}/${networkId}?token=${encodeURIComponent(token)}`;
    }, [networkId, token, isV2]);

    const v2 = useV2TradeNotifications({
        enabled: isV2,
        networkId,
        token,
        onTradeUpdate,
        onConnected,
        onError,
        onExecutionPhase,
        onReconnect,
    });

    const callbacksRef = useRef({
        onTradeUpdate,
        onConnected,
        onError,
        onExecutionPhase,
        onReconnect,
    });
    useEffect(() => {
        callbacksRef.current = {
            onTradeUpdate,
            onConnected,
            onError,
            onExecutionPhase,
            onReconnect,
        };
    });

    const [lastTradeUpdate, setLastTradeUpdate] =
        useState<TradeNotificationUpdate | null>(null);

    const prevStateRef = useRef<string>('disconnected');

    const handleMessage = useCallback((msg: TradeNotificationMessage) => {
        switch (msg.type) {
        case 0:
            callbacksRef.current.onConnected?.(msg);
            break;
        case 1:
            setLastTradeUpdate(msg);
            callbacksRef.current.onTradeUpdate?.(msg);
            break;
        case 2:
            callbacksRef.current.onError?.(msg);
            break;
        case 3:
            break;
        case 4:
            callbacksRef.current.onExecutionPhase?.(msg);
            break;
        }
    }, []);

    const handleStateChange = useCallback((newState: string) => {
        if (
            prevStateRef.current === 'reconnecting' &&
            newState === 'connected'
        ) {
            callbacksRef.current.onReconnect?.();
        }
        prevStateRef.current = newState;
    }, []);

    const { state: connectionState, send } =
        useWebSocket<TradeNotificationMessage>(url, {
            transform: parseTradeNotification,
            onMessage: handleMessage,
            onStateChange: handleStateChange,
            reconnect: true,
            reconnectInterval: 2000,
            maxReconnectAttempts: 0,
        });

    // Client-initiated keepalive ping every 30s
    useEffect(() => {
        if (connectionState !== 'connected') return;

        const interval = setInterval(() => {
            try {
                send(JSON.stringify({ type: 3 }));
            } catch {
                // send may throw if the connection dropped between the
                // state check and the actual send call — safe to ignore
            }
        }, KEEPALIVE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [connectionState, send]);

    const isConnected = connectionState === 'connected';

    if (isV2) return v2;

    return {
        connectionState,
        isConnected,
        lastTradeUpdate,
    };
}
